<template>
  <div class="top-menu-desktop">
    <ul class="nav navbar-nav main-menu">
      <li
        v-for="item in menu"
        :key="item.id"
        class="menu-item item-depth-0"
        :class="{ active: currentItem.id == item.id }"
        @mouseover="showSubmenu(item)"
        @mouseleave="hideSubmenu"
      >
        <a :href="item.url" class="nav-link-text" v-html="item.title" />
      </li>
    </ul>
    <transition name="fade-basic">
      <div v-show="showMenu" class="top-menu-desktop__mask" />
    </transition>
    <transition name="slide-menu">
      <div
        v-if="showMenu"
        class="top-menu-desktop__submenu"
        :style="`height: ${submenuHeight}px`"
        @mouseover="showSubmenu(false)"
        @mouseleave="hideSubmenu(false)"
      >
        <transition name="fade-menu-content" appear>
          <div
            :key="currentItem.id"
            ref="container"
            class="top-menu-desktop__container"
            :class="{ 'not-active': !currentItem.children.length }"
          >
            <div class="container">
              <div class="row">
                <div
                  v-if="currentItem.general || customDescr"
                  class="top-menu-desktop__description"
                  :class="wideDescr ? 'col-md-6' : 'col-md-3'"
                >
                  <div
                    class="top-menu-desktop__description-title"
                    v-html="
                      currentItem.general && currentItem.general.title_long
                        ? currentItem.general.title_long
                        : currentItem.title
                    "
                  />
                  <div
                    v-if="currentItem.general.description || customDescr"
                    class="top-menu-desktop__description-body"
                    v-html="customDescr || currentItem.general.description"
                  />
                  <a
                    v-if="
                      !customUrl &&
                      currentItem.general &&
                      currentItem.general.link
                    "
                    class="btn btn-cta top-menu-desktop__description-btn"
                    :href="currentItem.general.link.url"
                    v-html="currentItem.general.link.title"
                  />
                  <a
                    v-if="customUrl"
                    class="btn btn-cta top-menu-desktop__description-btn"
                    :href="customUrl"
                    v-html="customLabel"
                  />
                </div>
                <div
                  class="top-menu-desktop__body"
                  :class="wideDescr ? 'col-md-6' : 'col-md-9'"
                >
                  <div class="row">
                    <div
                      v-for="item in currentItem.children"
                      :key="item.id"
                      class="top-menu-desktop__sublist"
                      :class="wideDescr ? 'col-md-6' : 'col-md-4'"
                    >
                      <div
                        class="top-menu-desktop__sublist-title"
                        v-html="item.title"
                      />
                      <ul
                        v-if="item.children.length"
                        class="top-menu-desktop__sublist-list"
                      >
                        <li
                          v-for="childItem in item.children.slice(0, 4)"
                          :key="childItem.id"
                        >
                          <a
                            class="link-purple-color top-menu-desktop__sublist-link"
                            :href="childItem.url"
                            v-html="childItem.title"
                          />
                        </li>
                      </ul>
                      <a
                        v-if="isProperLink(item.url)"
                        class="arrow-link top-menu-desktop__more-link"
                        :href="item.url"
                        >{{ texts.showAll }}</a
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { useCityTermLocalStorage } from 'Main/composables/useCityTermLocalStorage';
import { mapState } from 'vuex';

const TIME_FOR_KEEPING_MENU_OPENED = 700;
const TIME_FOR_PREVENTING_MENU_OPENING = 400;
const TIME_FOR_PREVENTING_MENU_SWITCHING = 150;
const PATH_FUNERAL_MENU = 'begravning';
const PATH_LAW_MENU = 'juridik';

export default {
  name: 'TopMenuDesktop',

  setup() {
    const {
      cityTerm,
      saveCityTermToLocalStorage,
      getCityTermFromLocalStorage,
    } = useCityTermLocalStorage();
    return {
      cityTerm,
      saveCityTermToLocalStorage,
      getCityTermFromLocalStorage,
    };
  },

  data() {
    return {
      isMouseOut: true,
      isMouseIn: false,
      currentItem: {},
      submenuHeight: 0,
      preventOpeningTimeout: null,
      preventSwitchingTimeout: null,
      preventHidingTimeout: null,
      texts: theme_vars.topMenuDesktop,
    };
  },

  computed: {
    ...mapState('cities', ['cityLinks']),

    menu() {
      return window.menuData || [];
    },

    showMenu() {
      return Boolean(
        !this.isMouseOut &&
        this.currentItem.children &&
        this.currentItem.children.length
      );
    },

    currentPath() {
      if (this.currentItem.url) {
        const pathArr = this.currentItem.url.split('/');

        return pathArr[pathArr.length - 1] || pathArr[pathArr.length - 2];
      }
      return null;
    },

    customDescr() {
      // Funeral desc.
      if (this.cityLinks && [PATH_FUNERAL_MENU].includes(this.currentPath)) {
        return `Vi erfarna rådgivare i ${this.cityLinks.funeral.title} hjälper dig att förverkliga det avsked som just du önskar.`;
      }

      // Law desc.
      if (this.cityLinks && [PATH_LAW_MENU].includes(this.currentPath)) {
        return `Vi kunniga jurister i ${this.cityLinks.funeral.title} hjälper dig som behöver juridisk rådgivning. Vi har omfattande kompetens inom såväl familjerätt och affärsjuridik.`;
      }

      return null;
    },

    customLabel() {
      if (!this.customDescr || !this.cityLinks) {
        return null;
      }

      return this.currentPath === PATH_FUNERAL_MENU
        ? 'Hitta din rådgivare'
        : 'Hitta din jurist';
    },

    customUrl() {
      if (!this.customDescr || !this.cityLinks) {
        return null;
      }

      return this.currentPath === PATH_FUNERAL_MENU
        ? this.cityLinks.funeral.url
        : this.cityLinks.lawyer.url;
    },

    wideDescr() {
      return this.currentItem.children.length < 3;
    },
  },

  watch: {
    currentItem() {
      this.$nextTick(() => {
        this.submenuHeight = this.$refs.container
          ? this.$refs.container.offsetHeight
          : 0;
      });
    },
  },

  methods: {
    showSubmenu(item) {
      this.isMouseIn = true;
      this.isMouseOut = false;

      if (this.cityTerm && !this.cityLinks) {
        this.$store.dispatch('cities/getCityLink', this.cityTerm.id);
      }

      if (!item) {
        clearTimeout(this.preventOpeningTimeout);
        clearTimeout(this.preventSwitchingTimeout);
        return;
      }

      if (item.id === this.currentItem.id) {
        clearTimeout(this.preventHidingTimeout);
      }

      if (
        item &&
        this.currentItem.children &&
        this.currentItem.children.length
      ) {
        clearTimeout(this.preventOpeningTimeout);
        this.preventSwitchingTimeout = setTimeout(() => {
          this.setCurrentItem(item);
        }, TIME_FOR_PREVENTING_MENU_SWITCHING);
      } else {
        this.preventOpeningTimeout = setTimeout(() => {
          this.setCurrentItem(item);
        }, TIME_FOR_PREVENTING_MENU_OPENING);
      }
    },

    setCurrentItem(item) {
      if (item) {
        this.currentItem = item;
      }
    },

    hideSubmenu() {
      this.isMouseIn = false;

      clearTimeout(this.preventOpeningTimeout);

      this.preventHidingTimeout = setTimeout(() => {
        if (!this.isMouseIn) {
          this.isMouseOut = true;
          this.currentItem = {};
        }
      }, TIME_FOR_KEEPING_MENU_OPENED);
    },

    isProperLink(link) {
      return link && link[0] !== '#';
    },
  },
};
</script>
